import { useLanguageOptions } from "@/hooks";
import { useGetLanguageNativeLabel } from "@/hooks/useGetLanguageNativeLabel";
import { useLanguage, useT } from "@/i18n";
import { Language } from "@/i18n/i18n.types";
import { FlagIcon, Select } from "@/ui";

export type LanguageSelectProps = {
  allowedLanguages?: Language[];
  size?: "sm" | "md";
  isIconOnly?: boolean;
};

export const LanguageSelect = ({ allowedLanguages, isIconOnly = false }: LanguageSelectProps) => {
  const t = useT();
  const languageOptions = useLanguageOptions();
  const [language, changeLanguage] = useLanguage();
  const getLanguageLabel = useGetLanguageNativeLabel();

  const getTranslatedLanguageLabel = (locale: string): string => {
    try {
      return new Intl.DisplayNames([language], { type: "language" }).of(locale) ?? locale;
    } catch {
      return locale;
    }
  };

  const filteredLanguageOptions = allowedLanguages
    ? languageOptions.filter((option) => allowedLanguages.includes(option.languageKey))
    : languageOptions;

  const baseLangCounts = filteredLanguageOptions.reduce<Record<string, number>>((acc, option) => {
    const base = option.languageKey.split("-")[0];
    acc[base] = (acc[base] ?? 0) + 1;
    return acc;
  }, {});

  const selectedLanguageOption =
    filteredLanguageOptions.find((option) => option.languageKey === language) ??
    filteredLanguageOptions[0];

  return (
    <Select
      label={t("general.components.languageSelect.label")}
      isClearable={false}
      popoverProps={{
        width: "fit-content",
      }}
      placeholder={t("general.components.languageSelect.placeholder")}
      options={filteredLanguageOptions}
      getOptionValue={(option) => option.languageKey}
      getOptionLabel={(option) => {
        const base = option.languageKey.split("-")[0];
        const locale = baseLangCounts[base] > 1 ? option.languageKey : base;
        return getTranslatedLanguageLabel(locale);
      }}
      getOptionDescription={(option) => getLanguageLabel(option.languageKey)}
      getOptionIcon={(option, { width, height }) => (
        <FlagIcon width={width} height={height} countryCode={option.countryCode} />
      )}
      hideLabel={true}
      fullWidth={false}
      variant="transparent"
      isIconOnly={isIconOnly}
      selectedOption={selectedLanguageOption}
      disallowEmptySelection={true}
      onValueChange={(value) => {
        if (value === null) {
          console.error("Do not expect null value");
          return;
        }

        changeLanguage(value.languageKey);
      }}
    />
  );
};
